// functions/src/generation 응답 형식

export interface BudgetStatus {
  monthKey: string;
  limitKrw: number;
  spentKrw: number;
  reservedKrw: number;
}

export interface GenConfig {
  configured: boolean;
  models: { id: string; label: string; reserveKrw: number; transparent: boolean; /** 재작업 재생성용 모델 */ fallback?: boolean }[];
  maxImagesPerBatch: number;
  maxCandidates: number;
  budget: BudgetStatus;
}

export interface Estimate {
  total: number;
  modelId: string;
  fallbackTransparent: boolean;
  estimateKrw: number;
  maxEstimateKrw: number;
  budget: BudgetStatus;
  remainingKrw: number;
  affordable: boolean;
  vocab: { vocabId: string; text: string }[];
}

export interface GenBatch {
  batchId: string;
  kind?: 'auto' | 'manual';
  vocabIds: string[];
  candidates: number;
  modelId: string;
  fallbackTransparent: boolean;
  estimateKrw: number;
  maxEstimateKrw: number;
  createdBy: string;
  createdAt: string;
}

export type JobState = 'queued' | 'running' | 'succeeded' | 'failed';

export interface GenJob {
  jobId: string;
  batchId: string;
  vocabId: string;
  modelId: string;
  kind: 'primary' | 'transparent-fallback';
  state: JobState;
  error: string | null;
  assetIds: string[];
  outcome: 'succeeded' | 'rework' | null;
  reasons: string[];
  costKrw: number | null;
  createdAt: string;
}

export interface GenAsset {
  assetId: string;
  origin?: 'auto' | 'manual' | 'upload';
  vocabIds: string[];
  jobId: string;
  stage: string;
  parentAssetId: string | null;
  generation: { modelId: string; costKrw: number | null; prompt: string } | null;
  original: { width: number; height: number; format: string } | null;
  postprocess: { state: string; reasons: string[] } | null;
  originalUrl: string | null;
  finalUrl: string | null;
}

export interface BatchDetail {
  batch: GenBatch;
  jobs: GenJob[];
  assets: GenAsset[];
}

export interface RunNextResult {
  processed: boolean;
  remainingQueued: number;
}

export const JOB_STATE: Record<JobState, { label: string; color: string }> = {
  queued: { label: '대기', color: 'default' },
  running: { label: '생성 중', color: 'processing' },
  succeeded: { label: '완료', color: 'success' },
  failed: { label: '실패', color: 'error' },
};

export const STAGE_LABEL: Record<string, { label: string; color: string }> = {
  in_review: { label: '검수 대기', color: 'warning' },
  postprocess_failed: { label: '재작업(후처리)', color: 'orange' },
  rework: { label: '재작업', color: 'orange' },
  approved: { label: '승인', color: 'blue' },
  discarded: { label: '폐기', color: 'default' },
  published: { label: '게시 완료', color: 'success' },
};

export const won = (n: number) => `${Math.round(n).toLocaleString('ko-KR')}원`;

/** 배치 진행 요약 */
export function batchProgress(jobs: readonly GenJob[]) {
  const c = { queued: 0, running: 0, succeeded: 0, failed: 0 };
  for (const j of jobs) c[j.state]++;
  const cost = jobs.reduce((n, j) => n + (j.costKrw ?? 0), 0);
  return { ...c, total: jobs.length, done: c.succeeded + c.failed, cost };
}

/** 수동 제작 편집 영역(한글 가능). 서버가 공통 규칙과 합쳐 프롬프트를 만든다 */
export interface PromptParts {
  subject: string | null;
  extra: string | null;
}

/** 영문 프롬프트 문장과 한국어 번역(화면 표시용) */
export interface PromptSegment {
  en: string;
  ko: string;
}

/** GET /generation/prompt */
export interface PromptPreview {
  vocabId: string;
  modelId: string;
  /** 어휘 기본 편집 영역(생성 피사체 설명) */
  defaults: PromptParts;
  parts: PromptParts;
  /** 모델에 보내는 영문 프롬프트 */
  prompt: string;
  promptKo: string;
  /** 편집 영역으로 만든 문장 */
  subject: PromptSegment;
  /** 공통 규칙(수정 불가) */
  common: PromptSegment[];
}
